import {
  CdkDragDrop,
  CdkDragEnd,
  CdkDragEnter,
  CdkDragExit,
  CdkDragStart,
} from "@angular/cdk/drag-drop";
import { Inject, Injectable, Optional } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, EMPTY, Observable, Subject } from "rxjs";
import {
  API_BASE_URL,
  CardCategories,
  CardCollectionStatsResponse,
  CardDeckType,
  CardModel,
  CardModelResponse,
  CardSelectionType,
  CardsService,
  DailyPeriodDbResponse,
  EditCardMode,
  EditCardOutcome,
  EditCardRequest,
  EditCardResponse,
  MyDeckModel,
  PatientPlanService,
} from "../../../swagger/api.client.generated";
import { CardSize, Size, SizeHeight } from "./card.models";
import { BLANK_CARD_NAME, isBlankCardName } from "./card-type.utils";
import { catchError, take, tap } from "rxjs/operators";
import { HelperFunctions } from "../functions/helper.functions";
import { SnackbarService } from "@app/common/snackbar-custom/snackbar.service";

@Injectable({
  providedIn: "root",
})
export class CardLocalService {
  private cardsLoadedSource = new Subject();
  private cardsChangedSource = new Subject<void>();
  public cardFlipSound: HTMLAudioElement;
  onCardsLoaded = this.cardsLoadedSource.asObservable();
  /** Fires whenever local custom cards are upserted into / cleared from the in-memory cache. */
  onCardsChanged$ = this.cardsChangedSource.asObservable();
  private customCardCreatedSource = new Subject<number | undefined>();
  /**
   * Fires with the deck id (1 activity, 2 safety) each time a custom card is created on the
   * server - from the card library, the pickers or the Plan. Onboarding's shared
   * `custom_card_saved` milestone listens (P07, FLIP-358).
   */
  customCardCreated$ = this.customCardCreatedSource.asObservable();
  onDragEntered = new Subject<CdkDragEnter<CardModel[], CardModel>>();
  onDragExited = new Subject<CdkDragExit<CardModel[], CardModel>>();
  onDragDropped = new Subject<
    CdkDragDrop<CardModel[], CardModel[], CardModel>
  >();
  onDragStarted = new Subject<CdkDragStart<CardModel>>();
  onDragEnded = new Subject<CdkDragEnd<CardModel>>();
  dailyPeriodsStore = new BehaviorSubject<DailyPeriodDbResponse[]>([]);

  cardSize: Size;

  private IsLoaded: boolean = false;
  private cards: CardModel[] = [];
  private cardCategories: CardCategories[] = [];
  private myCardFilter: CardSelectionType[] = [];
  private cardDeckTypes: CardDeckType[] = [];
  private dailyPeriods: DailyPeriodDbResponse[] = [];
  private localCustomCardGuids: Set<string> = new Set();
  private baseUrl: string;

  constructor(
    private cardService: CardsService,
    private patientPlanService: PatientPlanService,
    private snackbar: SnackbarService,
    private http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.baseUrl = baseUrl ?? "";
    this.cardFlipSound = new Audio("assets/flipcard-sound.mp3");
    this.cardFlipSound.volume = 0.5;
  }

  // Optimistically toggles isPatientFavourite (and isPatientDeck on favourite-on),
  // then persists via savePatientCards. Reverts and snackbars on failure.
  // Replaces the card in the internal array with a shallow clone so OnPush
  // components (carousel card-shells) detect a new input reference.
  toggleFavorite(card: CardModel): Observable<number> {
    if (!card) return EMPTY;
    const prevFav = card.isPatientFavourite;
    const prevDeck = card.isPatientDeck;
    card.isPatientFavourite = !prevFav;
    if (card.isPatientFavourite) card.isPatientDeck = true;

    const idx = this.cards.findIndex((c) => c.cardId === card.cardId);
    if (idx >= 0) this.cards[idx] = Object.assign(new CardModel(), card);
    this.cardsChangedSource.next();

    const payload = new MyDeckModel();
    payload.cardModelList = [card];
    return this.cardService.savePatientCards(payload).pipe(
      take(1),
      catchError(() => {
        card.isPatientFavourite = prevFav;
        card.isPatientDeck = prevDeck;
        if (idx >= 0) this.cards[idx] = Object.assign(new CardModel(), card);
        this.cardsChangedSource.next();
        this.snackbar.openDesignSnackbar(
          "We encountered an issue",
          "Could not save favourite. Please try again."
        );
        return EMPTY;
      })
    );
  }

  // Soft-deletes a card from the user's library. Used by the card picker only — the card stops
  // appearing for future use but existing plans still reference it (the plan-render SP does not
  // filter Cards.IsActive).
  //
  // Deactivates the per-user PatientCards link (PatientCard.IsActive=0) — never the shared Cards
  // record. We persist via SavePatientCards (PatientCardSave), which UPSERTS: it creates the link
  // row if the user never added/favourited the card. The dedicated [dbo].[CardDelete] SP is
  // UPDATE-only, so it no-ops (and the delete silently fails) for system cards with no row yet.
  // IsActive is carried by IsPatientDeck (see CardSPRepository.SaveCard / PatientCardFlagHydrator),
  // so clearing isPatientDeck + isPatientFavourite marks the link inactive.
  deleteCard(card: CardModel): Observable<number> {
    if (!card) return EMPTY;
    const prevFav = card.isPatientFavourite;
    const prevDeck = card.isPatientDeck;
    card.isPatientFavourite = false;
    card.isPatientDeck = false;

    const payload = new MyDeckModel();
    payload.cardModelList = [card];
    return this.cardService.savePatientCards(payload).pipe(
      take(1),
      tap(() => {
        const idx = this.cards.findIndex((c) => c.cardId === card.cardId);
        if (idx >= 0) {
          this.cards.splice(idx, 1);
          this.cards = [...this.cards]; // new array ref for OnPush consumers
          this.cardsChangedSource.next();
        }
      }),
      catchError(() => {
        card.isPatientFavourite = prevFav;
        card.isPatientDeck = prevDeck;
        this.snackbar.openDesignSnackbar(
          "We encountered an issue",
          "Could not delete card. Please try again."
        );
        return EMPTY;
      })
    );
  }

  get ServiceLoaded() {
    return this.IsLoaded;
  }

  getCards(): CardModel[] {
    return this.cards;
  }

  /**
   * Cards the picker currently shows — used for the edit dialog's client-side duplicate-name check.
   * Returns the full loaded set (not just isPatientDeck): the server already drops cards the patient
   * has removed, so this.cards IS the visible set, and it includes standard library cards
   * (isPatientDeck=false) that are valid name clashes for a new/edited custom card. Keep aligned
   * with the backend HasDuplicateCardName check.
   */
  getDeckSnapshot(): CardModel[] {
    return this.cards;
  }

  /**
   * Registers or refreshes a named custom plan card in the in-memory deck so getDeckSnapshot()
   * includes it for duplicate-name validation. User-defined cards loaded from a saved plan come
   * through the plan-render endpoint (not getAllCards), so they would otherwise be absent from
   * this.cards. No-op for blank template placeholders. Keyed by cardId, falling back to cardGuid.
   */
  registerPlanCard(card: CardModel): void {
    if (!card || isBlankCardName(card.cardName ?? "")) return;
    // Skip unsaved in-plan custom cards (editable templates). Every custom activity card in a
    // category is cloned from the one "Custom" template, so they all share that template's cardId
    // and keep isEditableTemplate=true until plan-save (buildSaveItem relies on it to send cardId=null
    // so HandleNewCardCreation creates the row). Keying by the shared cardId below would collapse them
    // onto a single slot and, by copying the rendered card's cardGuid onto it, duplicate the
    // (cardId, cardGuid) trackBy key — corrupting the carousel/picker *ngFor once ≥2 coexist.
    // upsertLocalCustomCard already registers these keyed by their unique minted cardGuid.
    if (card.isEditableTemplate) return;
    const idx = card.cardId
      ? this.cards.findIndex(c => c.cardId === card.cardId)
      : card.cardGuid
        ? this.cards.findIndex(c => c.cardGuid === card.cardGuid)
        : -1;
    const clone = Object.assign(new CardModel(), card);
    // The library deck feeds the carousel and card-picker, which render the completion
    // star straight off cardModel.isComplete (card-shell.component.html). isComplete is
    // plan-session state — it belongs to a period's own card instance, never to the
    // library card — so strip it from the deck clone. Without this, completing a custom
    // card in a plan leaks the star into the carousel/picker (non-custom cards never
    // register a plan card, so they never leak). Mirrors how custom cards arrive from
    // getAllCards with isComplete unset. Does not touch `card`, so the plan period's own
    // instance keeps its star.
    clone.isComplete = false;
    if (idx >= 0) {
      this.cards[idx] = clone;
    } else {
      this.cards.push(clone);
    }
  }

  /**
   * Upserts a renamed custom card into the in-memory deck cache so the picker
   * surfaces it within the same edit session. Local-only; no API call. Keyed
   * by cardGuid (per-instance id assigned when the card is added to a plan).
   */
  upsertLocalCustomCard(card: CardModel): void {
    if (!card || !card.cardGuid) return;

    if (!this.localCustomCardGuids.has(card.cardGuid)) {
      // First-time upsert: the in-plan card may share its cardGuid with the
      // server-loaded "custom" placeholder (same value, or — when editing directly
      // from the picker — the exact same object reference). Capture the original guid
      // before any mutation so we can locate and restore the template entry.
      const originalGuid = card.cardGuid;

      // If there is an editable-template entry in the cache with this guid, restore
      // it to its blank state. Without this, mutating card.cardGuid below (which may
      // be the same object reference as the template) leaves a ghost entry whose guid
      // matches the pushed clone, producing a visible duplicate in the picker.
      //
      // Also scope by cardCategoryId: every unedited template loaded from the DB shares
      // the same (unset) cardGuid, so guid alone can't tell one category's template apart
      // from another's. Without the category check, creating a custom card in category B
      // could match and blank out an already-created card in category A.
      const templateIdx = this.cards.findIndex(
        (c) =>
          c.cardGuid === originalGuid &&
          c.isEditableTemplate &&
          c.cardCategory?.cardCategoryId === card.cardCategory?.cardCategoryId
      );
      if (templateIdx >= 0) {
        // Replace the (potentially in-place-mutated) template with a clean blank clone.
        // After this assignment `card` — if it was the same reference — is detached
        // from the array, so the card.cardGuid mutation below is safe.
        this.cards[templateIdx] = Object.assign(new CardModel(), this.cards[templateIdx], {
          cardGuid: originalGuid,
          cardName: BLANK_CARD_NAME,
        });
      }

      // Race guard: processSafetyPlanItems(true) runs getCardsFromDbObservable() after
      // saving the safety plan, which replaces this.cards[] with server data and clears
      // localCustomCardGuids. If that completes before this callback fires, the card is
      // already in the cache (by cardId) and a push here would produce a visible duplicate.
      // Exclude editable templates: a template can share a cardId with the card being
      // upserted without that meaning the user-defined card is already persisted.
      if (card.cardId && this.cards.some((c) => c.cardId === card.cardId && !c.isEditableTemplate)) {
        // Already present from the server fetch; just ensure the picker refreshes.
        this.cardsChangedSource.next();
        return;
      }

      // Mint a unique local guid. The in-plan card is mutated to the new guid so
      // re-edits resolve to the same cache entry.
      const localGuid = HelperFunctions.getGuid();
      card.cardGuid = localGuid;
      const clone = Object.assign(new CardModel(), card);
      this.cards.push(clone);
      this.localCustomCardGuids.add(localGuid);
    } else {
      // Re-edit of a card we've already added locally. Replace in place.
      const idx = this.cards.findIndex(
        (c) => c.cardGuid && c.cardGuid === card.cardGuid
      );
      const clone = Object.assign(new CardModel(), card);
      if (idx >= 0) {
        this.cards[idx] = clone;
      } else {
        this.cards.push(clone);
      }
    }
    this.cardsChangedSource.next();
  }

  hasLocalCustomCard(cardGuid: string | undefined | null): boolean {
    if (!cardGuid) return false;
    return this.cards.some((c) => c.cardGuid === cardGuid);
  }

  createCustomCardInDb(card: CardModel, deckTypeId?: number): Observable<number> {
    const deck = deckTypeId ?? card.cardDeckType?.cardDeckId;
    return this.http
      .post<number>(`${this.baseUrl}/api/Cards/CreateCustomCard`, {
        cardName: card.cardName,
        cardCategoryId: card.cardCategory?.cardCategoryId,
        periodMinutes: card.periodMinutes,
        deckTypeId: deck,
        cardDescription: card.cardDescription,
      })
      .pipe(
        tap((cardId) => {
          if (cardId > 0) {
            this.customCardCreatedSource.next(deck);
          }
        })
      );
  }

  // Persists a brand-new custom card created from the Card Library deck-management dialogs
  // (DialogActivityCards / DialogSafetyCards — editMode=false), then refetches the deck from the
  // server so the card becomes server truth in cards[]. This is deliberately NOT upsertLocalCustomCard:
  // that only tracks the card in-memory (localCustomCardGuids), and PlanLifecycleService.handleRoute
  // calls clearLocalCustomCards() on the next plan navigation, which would drop it. Refetching (mirrors
  // reconcileAfterEdit) leaves the card untracked, so it survives navigation and refresh, and emits
  // onCardsChanged$ so any open picker/carousel/deck dialog updates. Surfaces failures instead of
  // silently faking success.
  createLibraryCustomCard(card: CardModel, deckTypeId: number): Observable<number> {
    return this.createCustomCardInDb(card, deckTypeId).pipe(
      take(1),
      tap(() => {
        this.getCardsFromDbObservable().subscribe(() => this.cardsChangedSource.next());
      }),
      catchError(() => {
        this.snackbar.openDesignSnackbar("We encountered an issue", "Could not create card. Please try again.");
        return EMPTY;
      })
    );
  }

  // Edits or duplicates an existing saved card. The backend decides the outcome (update in place,
  // new version, copy-on-write, mandatory period override, or duplicate); we reconcile the in-memory
  // deck from the response and emit onCardsChanged$ so the picker/carousel refresh.
  editCard(
    card: CardModel,
    result: { cardName: string; periodMinutes?: number; mode: "edit" | "duplicate"; description?: string }
  ): Observable<EditCardResponse> {
    if (!card) return EMPTY;

    const request = new EditCardRequest();
    request.cardId = card.cardId;
    request.cardName = result.cardName;
    request.periodMinutes = result.periodMinutes ?? 0;
    request.cardCategoryId = card.cardCategory?.cardCategoryId;
    request.mode =
      result.mode === "duplicate" ? EditCardMode.Duplicate : EditCardMode.DirectEdit;
    request.cardDescription = result.description;

    return this.cardService.editCard(request).pipe(
      take(1),
      tap((response) => {
        if (!response) return;
        if (response.validationError) {
          this.snackbar.openDesignSnackbar(
            "We encountered an issue",
            response.validationError
          );
          return;
        }
        this.reconcileAfterEdit(card, result, response);
      }),
      catchError(() => {
        this.snackbar.openDesignSnackbar(
          "We encountered an issue",
          "Could not save card. Please try again."
        );
        return EMPTY;
      })
    );
  }

  // In-place outcomes mutate the cached entry for instant feedback; create outcomes (duplicate /
  // new version / copy-on-write) refetch the deck so server truth (new card present, deactivated
  // one gone) drives the picker.
  private reconcileAfterEdit(
    card: CardModel,
    result: { cardName: string; periodMinutes?: number; description?: string },
    response: EditCardResponse
  ): void {
    if (
      response.outcome === EditCardOutcome.InPlaceUpdate ||
      response.outcome === EditCardOutcome.MandatoryOverride
    ) {
      const idx = this.cards.findIndex((c) => c.cardId === card.cardId);
      if (idx >= 0) {
        const updated = this.cards[idx];
        if (response.outcome === EditCardOutcome.InPlaceUpdate) {
          updated.cardName = result.cardName;
          updated.cardDescription = result.description ?? undefined;
        }
        updated.periodMinutes = result.periodMinutes ?? 0;
        this.cards[idx] = Object.assign(new CardModel(), updated);
        this.cardsChangedSource.next();
      }
      return;
    }

    // A new card was created. Refetch so the deck reflects the new card and any deactivation, then
    // notify onCardsChanged$ subscribers (the picker re-pulls getCards() on that event).
    this.getCardsFromDbObservable().subscribe(() => this.cardsChangedSource.next());
  }


  /**
   * Removes every locally-upserted custom card from the in-memory cache. Called when
   * the user discards plan edits (PlanLifecycleService.handleRoute) so the carousel
   * and picker stop showing entries that were never persisted.
   */
  clearLocalCustomCards(): void {
    if (this.localCustomCardGuids.size === 0) return;
    this.cards = this.cards.filter(
      (c) => !c.cardGuid || !this.localCustomCardGuids.has(c.cardGuid)
    );
    this.localCustomCardGuids.clear();
    this.cardsChangedSource.next();
  }

  getFavouriteCards(): CardModel[] {
    const favourites = this.cards.filter(
      (a) =>
        (a.isPatientFavourite && a.cardDeckType.cardDeckId === 2) ||
        (a.isPatientFavourite &&
          a.cardDeckType.cardDeckId === 3 &&
          a.cardCategory.cardCategoryId !== 1 &&
          a.cardCategory.cardCategoryId !== 7)
    );
    return favourites;
  }

  getAngelCards(): CardModel[] {
    return this.cards.filter((a) => a.cardDeckType.cardDeckId === 2);
  }

  getActivityCards(): CardModel[] {
    return this.cards.filter((a) => a.cardDeckType.cardDeckId === 1);
  }

  getCardCategories(): CardCategories[] {
    return this.cardCategories;
  }

  getCardTypes(
    angelOnly: boolean = false,
    activityOnly: boolean = false
  ): CardDeckType[] {
    if (activityOnly) {
      return this.cardDeckTypes.filter((a) => a.cardDeckId === 1);
    } else if (angelOnly) {
      return this.cardDeckTypes.filter((a) => a.cardDeckId === 2);
    } else {
      return this.cardDeckTypes;
    }
  }

  getMyCardFilters(): CardSelectionType[] {
    return this.myCardFilter;
  }

  getCollectionStats(): Observable<CardCollectionStatsResponse> {
    return this.cardService.getCollectionStats();
  }

  getDailyPeriods(): Observable<DailyPeriodDbResponse[]> {
    return this.dailyPeriodsStore.asObservable();
  }

  init() {
    this.getCardsFromDb();
    this.getDailyPeriodsFromDb();
  }

  dragStarted(e: CdkDragStart<CardModel>) {
    this.onDragStarted.next(e);
  }

  dragEnded(e) {
    this.onDragEnded.next(e);
  }

  dragExited(e: CdkDragExit<CardModel[], CardModel>) {
    this.onDragExited.next(e);
  }

  dragEntered(e: CdkDragEnter<CardModel[], CardModel>) {
    this.onDragEntered.next(e);
  }

  dragDropped(e: CdkDragDrop<CardModel[], CardModel[], CardModel>) {
    this.onDragDropped.next(e);
  }

  /** @deprecated Sets --card-height-override directly. Use CardScaleService (--card-font-size). Remove when card-old is retired. */
  setCardSizeOverride(newSize: Size) {
    this.cardSize = newSize;

    const { height, width } = this.getCardHeightAndWidth(newSize);

    document.documentElement.style.setProperty(
      "--card-height-override",
      `${height}rem`
    );
    document.documentElement.style.setProperty(
      "--card-width-override",
      `${width}rem`
    );
  }

  /** @deprecated Sets --card-height/--card-width directly. Use CardScaleService (--card-font-size). Remove when card-old is retired. */
  setCardSize(zoomPercentage: number): CardSize {
    let newSize: Size;
    if (zoomPercentage <= 50) {
      newSize = Size.xxsml;
    } else if (zoomPercentage === 75) {
      newSize = Size.xsml;
    } else if (zoomPercentage === 100) {
      newSize = Size.sml;
    } else if (zoomPercentage === 125) {
      newSize = Size.md;
    } else if (zoomPercentage >= 150) {
      newSize = Size.lrg;
    } else {
      newSize = Size.sml;
    }

    this.cardSize = newSize;
    return new CardSize(newSize);
  }

  private getCardHeightAndWidth(size: Size): { height: number; width: number } {
    const sizeKey = Size[size];
    let height = Number(SizeHeight[sizeKey as keyof typeof SizeHeight]);
    let width = Number(Size[sizeKey as keyof typeof Size]);

    return { height, width };
  }

  getCardSize(): Size {
    return this.cardSize;
  }

  isCardEmpty(card: CardModel): boolean {
    if (card === null || card === undefined) {
      return true;
    }
    return isBlankCardName(card.cardName);
  }

  getCardsFromDbObservable(): Observable<CardModelResponse> {
    return this.cardService.getAllCards().pipe(
      take(1),
      tap((cards) => {
        this.cards = cards.cards;
        this.cardCategories = cards.cardCategories;
        this.cardDeckTypes = cards.cardDeckTypes;
        this.myCardFilter = cards.cardSelectionType;
        // Server fetch supersedes any in-memory upserts.
        this.localCustomCardGuids.clear();
        this.cardsLoadedSource.next();
        this.IsLoaded = true;
        this.cards.forEach((card) => {
          if (card.cardName.length === 0 && card.isEditableTemplate) {
            card.cardName = BLANK_CARD_NAME;
          }
        });
      })
    );
  }

  private getDailyPeriodsFromDb() {
    this.patientPlanService.dailyPeriodSelect().pipe(take(1)).subscribe((a) => {
      this.dailyPeriods = a;
      this.dailyPeriodsStore.next(a);
    });
  }

  private getCardsFromDb() {
    this.getCardsFromDbObservable().subscribe();
  }
}
